import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Bed, Bath, Maximize, MapPin, ArrowRight } from 'lucide-react';
import { formatCurrency } from '../utils/helpers';

const PropertyCard = ({ property }) => {
  const isSold = property.status === 'sold';

  const getPropertyTypeLabel = (type) => {
    const labels = {
      house: 'Casa',
      apartment: 'Apartamento',
      land: 'Terreno',
      hotel: 'Hotel'
    };
    return labels[type] || type;
  };

  return (
    <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 bg-white" data-testid={`property-card-${property.id}`}>
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={property.images[0]}
          alt={property.title}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            isSold ? 'grayscale group-hover:scale-105' : 'group-hover:scale-110'
          }`}
        />
        <div className="absolute top-4 left-4 flex gap-2 z-10">
          <Badge className="bg-golden-600 hover:bg-golden-700 text-white border-0">
            {getPropertyTypeLabel(property.type)}
          </Badge>
          {property.featured && !isSold && (
            <Badge className="bg-white/95 text-gray-900 border-0">
              Destacada
            </Badge>
          )}
        </div>

        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20" data-testid={`sold-stamp-card-${property.id}`}>
            <div className="bg-green-700 text-white font-extrabold tracking-[0.3em] text-2xl md:text-3xl px-12 py-3 border-4 border-white shadow-2xl -rotate-[18deg] uppercase">
              Vendida
            </div>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-golden-600 transition-colors">
              {property.title}
            </h3>
            <div className="flex items-center text-gray-600 text-sm">
              <MapPin className="w-4 h-4 mr-1" />
              <span>{property.location}</span>
            </div>
          </div>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {property.description}
        </p>

        {property.type !== 'land' && (
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
            {property.bedrooms && (
              <div className="flex items-center text-gray-600">
                <Bed className="w-4 h-4 mr-1.5" />
                <span className="text-sm font-medium">{property.bedrooms}</span>
              </div>
            )}
            {property.bathrooms && (
              <div className="flex items-center text-gray-600">
                <Bath className="w-4 h-4 mr-1.5" />
                <span className="text-sm font-medium">{property.bathrooms}</span>
              </div>
            )}
            <div className="flex items-center text-gray-600">
              <Maximize className="w-4 h-4 mr-1.5" />
              <span className="text-sm font-medium">{property.area}m²</span>
            </div>
          </div>
        )}

        {property.type === 'land' && (
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-center text-gray-600">
              <Maximize className="w-4 h-4 mr-1.5" />
              <span className="text-sm font-medium">{property.area}m²</span>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <p className={`text-2xl font-bold ${isSold ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
              {formatCurrency(property.price)}
            </p>
            {isSold ? (
              <p className="text-xs text-red-600 font-semibold mt-1 uppercase tracking-wider">
                Propiedad Vendida
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Aparta desde {formatCurrency(15000)}
              </p>
            )}
          </div>
          <Button asChild className="bg-golden-600 hover:bg-golden-700 text-white group/btn" data-testid={`property-card-details-btn-${property.id}`}>
            <Link to={`/propiedad/${property.id}`}>
              Ver Detalles
              <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyCard;
